// ============================================================================
// 📦 1. 라이브러리 및 전역 설정
// ============================================================================
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle, CheckCircle2, Clock3, Plus, Settings, Trash2, X, Flame, FilterX,
  MessageSquare, Send, User as UserIcon, ZoomIn, ZoomOut, Zap, Activity, Save, GripVertical, RefreshCw
} from 'lucide-react';
import { crmDataService } from '../services/crmDataService';
import PermissionGate from './PermissionGate'; // 🚨 [추가] 자물쇠 마법상자 불러오기

type TeamStatus = 'completed' | 'in_progress' | 'imminent' | 'delayed' | 'upcoming';

interface TeamProgressItem {
  team: string;
  expectedStartDate: string;
  expectedEndDate: string;
  completedDate: string;
  note: string;
  isUrgent?: boolean;
}

interface ProjectProgress {
  id: string;
  projectName: string;
  clientName: string;
  itemName: string;
  marketingOwner?: string;
  teams: TeamProgressItem[];
}

interface ProjectProgressBoardProps {
  globalRefreshKey?: number;
  currentUser?: any;
  projects: ProjectProgress[];  // 👈 부모가 주는 원본 데이터
  onRefresh: () => void;        // 👈 저장이 끝나면 부모를 깨우는 함수
}

const TEAM_ORDER = ['마케팅', '기구설게', '전장설계','구매(제품)', '구매(상품)', '제조', '제어', 'XR', 'DX'];
const BASE_DAY_WIDTH = 44; 

// ============================================================================
// 🛠️ 2. 유틸리티 함수 (날짜 계산 및 상태 판별기)
// ============================================================================
const parseDate = (value: string): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const getMidnight = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const getDayDiff = (targetDate: Date, baseDate: Date) => {
  const t = getMidnight(targetDate).getTime();
  const b = getMidnight(baseDate).getTime();
  return Math.round((t - b) / (1000 * 60 * 60 * 24));
};

const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max);

const getTeamStatus = (team: TeamProgressItem): TeamStatus => {
  const today = getMidnight(new Date());
  const start = team.expectedStartDate ? getMidnight(parseDate(team.expectedStartDate)!) : null;
  const end = team.expectedEndDate ? getMidnight(parseDate(team.expectedEndDate)!) : null;
  const completed = team.completedDate ? getMidnight(parseDate(team.completedDate)!) : null;

  if (completed) return 'completed';
  if (end && today > end) return 'delayed';
  if (!start && !end) return 'upcoming'; 
  if (start && today < start) return 'upcoming';
  if (end) {
    const remain = getDayDiff(end, today);
    if (remain >= 0 && remain <= 3) return 'imminent';
  }
  return 'in_progress'; 
};

const getDdayLabel = (team: TeamProgressItem): string => {
  const end = parseDate(team.expectedEndDate);
  const completed = parseDate(team.completedDate);
  if (completed) return '완료';
  if (!end) return '예정';

  const today = new Date();
  const diff = getDayDiff(end, today);
  
  if (diff > 0) return `D-${diff}`;
  if (diff < 0) return `🚨 D+${Math.abs(diff)} 지연`;
  return 'D-Day';
};

const formatMonthLabel = (date: Date) => `${date.getFullYear()}년 ${date.getMonth() + 1}월`;

const statusStyleMap: Record<TeamStatus, string> = {
  completed: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
  in_progress: 'bg-blue-100 text-blue-700 border border-blue-200',
  imminent: 'bg-amber-100 text-amber-700 border border-amber-200',
  delayed: 'bg-rose-100 text-rose-700 border border-rose-200',
  upcoming: 'bg-slate-100 text-slate-700 border border-slate-200',
};

const statusLabelMap: Record<TeamStatus, string> = {
  completed: '완료',
  in_progress: '진행중',
  imminent: '종료 임박',
  delayed: '지연',
  upcoming: '예정',
};

const isProjectCompleted = (project: ProjectProgress): boolean => {
  const activeTeams = project.teams.filter(t => t.expectedStartDate || t.expectedEndDate || t.completedDate || t.isUrgent || t.note);
  if (activeTeams.length === 0) return false; 
  return activeTeams.every(t => !!t.completedDate);
};

const getProjectSummary = (project: ProjectProgress) => {
  let minStart: number | null = null;
  let maxEnd: number | null = null;
  const delayedTeams: string[] = [];
  const imminentTeams: string[] = [];

  project.teams.forEach(t => {
    const sMs = parseDate(t.expectedStartDate)?.getTime();
    const eMs = parseDate(t.expectedEndDate)?.getTime();
    const st = getTeamStatus(t);

    if (sMs && (!minStart || sMs < minStart)) minStart = sMs;
    if (eMs && (!maxEnd || eMs > maxEnd)) maxEnd = eMs;

    if (st === 'delayed') delayedTeams.push(t.team);
    if (st === 'imminent') imminentTeams.push(t.team);
  });

  const formatDt = (ms: number | null) => {
    if (!ms) return '';
    const d = new Date(ms);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  return { overallStart: formatDt(minStart), overallEnd: formatDt(maxEnd), delayedTeams, imminentTeams };
};

// ============================================================================
// 🚀 3. 메인 컴포넌트 시작
// ============================================================================
const ProjectProgressBoard: React.FC<ProjectProgressBoardProps> = ({ globalRefreshKey = 0, currentUser, projects: initialProjects, onRefresh }) => {
  
  // 🎯 [핵심 로직 추가] 1. 내부 상태를 부모가 준 데이터로 초기화
  const [projects, setProjects] = useState<ProjectProgress[]>(initialProjects);

  // 🎯 [핵심 로직 추가] 2. 부모 데이터(initialProjects)가 바뀌면 내 상태도 업데이트 (깜빡임 방지 핵심)
  useEffect(() => {
    setProjects(initialProjects);
  }, [initialProjects]);

  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false); // 💡 부모가 로딩을 관리하므로 기본값 false
  const [activeFilter, setActiveFilter] = useState<'all' | 'delayed' | 'imminent' | 'in_progress' | 'urgent' | 'completed'>('all');

  const [zoomPercent, setZoomPercent] = useState<number>(50);
  const dayWidth = BASE_DAY_WIDTH * (zoomPercent / 100);

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);
  const [expandedProjectMap, setExpandedProjectMap] = useState<Record<string, boolean>>({});
  
  const [historyModal, setHistoryModal] = useState<{ teamName: string; projectId: string } | null>(null);
  const [teamHistoryLogs, setTeamHistoryLogs] = useState<any[]>([]);
  const [newHistoryContent, setNewHistoryContent] = useState('');
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  const [pendingDeleteProjectId, setPendingDeleteProjectId] = useState<string | null>(null);
  
  const [draggingProjectId, setDraggingProjectId] = useState<string | null>(null);
  const [dragOverProjectId, setDragOverProjectId] = useState<string | null>(null);

  const [isSaving, setIsSaving] = useState(false);

  const timelineScrollRef = useRef<HTMLDivElement | null>(null);
  const timelineBodyScrollRef = useRef<HTMLDivElement | null>(null);
  const isDraggingTimelineRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartScrollLeftRef = useRef(0);
  const dragTargetScrollRef = useRef<HTMLDivElement | null>(null);
  const isSyncingScrollRef = useRef(false);
  const isProgrammaticScrollRef = useRef(false);

  const originalProjectRef = useRef<ProjectProgress | null>(null);

  // 🚨 [핵심 수정: 데이터 추출 헬퍼 함수] ieg_user_profile에서 이름을 정확히 꺼내옵니다.
  const getCurrentUserName = () => {
    const storedData = localStorage.getItem('ieg_user_profile');
    if (storedData) {
      try {
        const userData = JSON.parse(storedData);
        return userData.user?.name || userData.name || userData.username || currentUser?.name || '관리자';
      } catch (e) {
        return '관리자';
      }
    }
    return '관리자';
  };

  // 💡 [기존 직접 fetch 로직 제거됨] -> 이제 부모가 loadData(true)를 호출하여 initialProjects를 갱신합니다.

  // 🚨 [수동 저장 로직 - 깜빡임 완전 차단]
  const saveToServer = async (currentProjects: ProjectProgress[], silent = false) => {
    try {
      if (!silent) setIsSaving(true); 
      await crmDataService.saveProjectProgress(currentProjects);
      
      // ✅ [핵심] 내가 직접 가져오지 않고 부모의 새로고침 함수를 호출합니다.
      onRefresh(); 
      
      if (!silent) {
        alert("✅ 데이터가 성공적으로 저장되었습니다.");
      }
    } catch (error) {
      if (!silent) alert("❌ 저장에 실패했습니다. 네트워크를 확인해 주세요.");
    } finally {
      if (!silent) setIsSaving(false);
    }
  };

  const handleSaveChangesWithAudit = async () => {
    const orig = originalProjectRef.current;
    const curr = selectedProject;
    
    if (orig && JSON.stringify(orig) === JSON.stringify(curr)) {
      setIsSettingsOpen(false);
      return; 
    }

    if (isSaving) return;
    const userName = getCurrentUserName();

    if (orig && curr) {
      for (const currTeam of curr.teams) {
        const origTeam = orig.teams.find(t => t.team === currTeam.team);
        if (origTeam) {
          const changes: string[] = [];
          if (origTeam.expectedStartDate !== currTeam.expectedStartDate) changes.push(`시작일: ${origTeam.expectedStartDate || '미정'} ➡️ ${currTeam.expectedStartDate || '미정'}`);
          if (origTeam.expectedEndDate !== currTeam.expectedEndDate) changes.push(`종료일: ${origTeam.expectedEndDate || '미정'} ➡️ ${currTeam.expectedEndDate || '미정'}`);
          if (origTeam.completedDate !== currTeam.completedDate) changes.push(`완료일: ${origTeam.completedDate || '미정'} ➡️ ${currTeam.completedDate || '미정'}`);
          if (!!origTeam.isUrgent !== !!currTeam.isUrgent) changes.push(`긴급여부: ${origTeam.isUrgent ? '체크됨' : '해제됨'} ➡️ ${currTeam.isUrgent ? '체크됨' : '해제됨'}`);

          if (changes.length > 0) {
            const logMsg = `[시스템 자동기록 - ${userName}] 🔄 일정이 수정되었습니다.\n${changes.join('\n')}`;
            await crmDataService.addWbsTeamHistory(curr.id, currTeam.team, logMsg, userName).catch(console.log);
          }
        }
      }
    }

    // 💡 저장 버튼 클릭 시 부모 데이터 갱신 유도
    await saveToServer(projects, false);
    originalProjectRef.current = JSON.parse(JSON.stringify(curr));
  };


  const loadHistory = async (projectId: string, teamName: string) => {
    setIsHistoryLoading(true);
    try {
      const logs = await crmDataService.getWbsTeamHistory(projectId, teamName);
      setTeamHistoryLogs(logs || []);
    } catch (error) {
      setTeamHistoryLogs([]);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (historyModal) {
      loadHistory(historyModal.projectId, historyModal.teamName);
    } else {
      setTeamHistoryLogs([]);
      setNewHistoryContent('');
    }
  }, [historyModal]);

  const handleAddHistory = async () => {
    if (!historyModal || !newHistoryContent.trim()) return;

    const userName = getCurrentUserName();

    const success = await crmDataService.addWbsTeamHistory(
      historyModal.projectId, 
      historyModal.teamName, 
      newHistoryContent,
      userName 
    );

    if (success) {
      setNewHistoryContent(''); 
      // 💡 모달 내역 갱신 및 부모 데이터 백그라운드 갱신
      loadHistory(historyModal.projectId, historyModal.teamName); 
      onRefresh(); 
    } else {
      alert("기록 저장에 실패했습니다.");
    }
  };

  useEffect(() => {
    if (projects.length === 0) return;
    setExpandedProjectMap((prev) => {
      const next: Record<string, boolean> = { ...prev };
      let changed = false;
      projects.forEach((project, idx) => {
        if (typeof next[project.id] !== 'boolean') {
            next[project.id] = idx < 2;
            changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [projects]);

  const selectedProject = useMemo(() => projects.find((project) => project.id === selectedProjectId) || projects[0], [projects, selectedProjectId]);

  const updateSelectedProjectMeta = (field: 'projectName' | 'clientName' | 'itemName' | 'marketingOwner', value: string) => {
    if (!selectedProject) return;
    setProjects((prev) => prev.map((project) => (project.id === selectedProject.id ? { ...project, [field]: value } : project)));
  };

  const updateTeamInProject = (projectId: string, teamName: string, updates: Partial<TeamProgressItem>) => {
    setProjects((prev) => prev.map((project) => {
      if (project.id !== projectId) return project;
      return { ...project, teams: project.teams.map((team) => (team.team === teamName ? { ...team, ...updates } : team)) };
    }));
  };
  
  const updateTeamField = (teamName: string, field: keyof TeamProgressItem, value: any) => {
    if (!selectedProject) return;
    updateTeamInProject(selectedProject.id, teamName, { [field]: value });
  };

  const addProject = () => {
    const index = projects.length + 1;
    const newProject: ProjectProgress = {
      id: `proj-${Date.now()}`,
      projectName: `신규 프로젝트 ${index}`,
      clientName: '',
      itemName: '',
      marketingOwner: '',
      teams: TEAM_ORDER.map((team) => ({
        team, expectedStartDate: '', expectedEndDate: '', completedDate: '', note: '', isUrgent: false
      })),
    };
    setProjects((prev) => [newProject, ...prev]);
    setSelectedProjectId(newProject.id);
    originalProjectRef.current = null;
    setExpandedProjectMap((prev) => ({ ...prev, [newProject.id]: true }));
    setActiveFilter('all');
  };

  const deleteProject = (projectId: string) => {
    const updatedProjects = projects.filter((project) => project.id !== projectId);
    setProjects(updatedProjects);
    saveToServer(updatedProjects, true); 
  };

  const addTeamByName = (teamName: string) => {
    if (!selectedProject || !teamName) return;
    if (selectedProject.teams.some((item) => item.team === teamName)) return;
    setProjects((prev) => prev.map((project) => project.id === selectedProject.id ? {
      ...project, teams: [...project.teams, { team: teamName, expectedStartDate: '', expectedEndDate: '', completedDate: '', note: '', isUrgent: false }],
    } : project));
  };

  const removeTeam = (teamName: string) => {
    if (!selectedProject) return;
    setProjects((prev) => prev.map((project) => project.id === selectedProject.id ? { ...project, teams: project.teams.filter((team) => team.team !== teamName) } : project));
  };

  const orderedTeams = useMemo(() => {
    if (!selectedProject) return [];
    return [...selectedProject.teams].sort((a, b) => TEAM_ORDER.indexOf(a.team) - TEAM_ORDER.indexOf(b.team));
  }, [selectedProject]);

  const missingTeams = useMemo(() => {
    if (!selectedProject) return [];
    return TEAM_ORDER.filter((team) => !selectedProject.teams.some((item) => item.team === team));
  }, [selectedProject]);

  const globalSummary = useMemo(() => {
    let delayed = 0, imminent = 0, in_progress = 0, urgent = 0, completed = 0;
    
    projects.forEach(p => {
      if (isProjectCompleted(p)) { completed++; return; }
      
      const hasDelayed = p.teams.some(t => getTeamStatus(t) === 'delayed');
      const hasImminent = p.teams.some(t => getTeamStatus(t) === 'imminent');
      const hasUrgent = p.teams.some(t => t.isUrgent && getTeamStatus(t) !== 'completed');
      const hasInProgress = p.teams.some(t => getTeamStatus(t) === 'in_progress');

      if (hasDelayed) delayed++;
      if (hasImminent && !hasDelayed) imminent++; 
      if (hasInProgress && !hasDelayed && !hasImminent) in_progress++; 
      if (hasUrgent) urgent++; 
    });
    
    return { delayed, imminent, in_progress, urgent, completed };
  }, [projects]);

  const filteredProjects = useMemo(() => {
    const sorted = [...projects].sort((a, b) => {
      const aDone = isProjectCompleted(a);
      const bDone = isProjectCompleted(b);
      if (aDone && !bDone) return 1;  
      if (!aDone && bDone) return -1; 
      return 0; 
    });

    if (activeFilter === 'all') return sorted;
    
    return sorted.filter(p => {
      const hasDelayed = p.teams.some(t => getTeamStatus(t) === 'delayed');
      const hasImminent = p.teams.some(t => getTeamStatus(t) === 'imminent');
      const hasUrgent = p.teams.some(t => t.isUrgent && getTeamStatus(t) !== 'completed');
      const hasInProgress = p.teams.some(t => getTeamStatus(t) === 'in_progress');

      if (activeFilter === 'completed') return isProjectCompleted(p);
      if (activeFilter === 'delayed') return hasDelayed;
      if (activeFilter === 'imminent') return hasImminent;
      if (activeFilter === 'urgent') return hasUrgent;
      if (activeFilter === 'in_progress') return hasInProgress;
      return false;
    });
  }, [projects, activeFilter]);

  const timelineBounds = useMemo(() => {
    const dates: Date[] = [];
    projects.forEach((project) => {
      project.teams.forEach((team) => {
        const s = parseDate(team.expectedStartDate);
        const e = parseDate(team.expectedEndDate);
        const c = parseDate(team.completedDate);
        if (s) dates.push(s);
        if (e) dates.push(e);
        if (c) dates.push(c);
      });
    });
    const today = new Date();
    const defaultStart = new Date(today); const defaultEnd = new Date(today);
    defaultStart.setDate(defaultStart.getDate() - 365 * 2);
    defaultEnd.setDate(defaultEnd.getDate() + 365 * 2);

    if (dates.length === 0) return { start: defaultStart, end: defaultEnd };
    const min = new Date(Math.min(...dates.map((d) => d.getTime()), defaultStart.getTime()));
    const max = new Date(Math.max(...dates.map((d) => d.getTime()), defaultEnd.getTime()));
    min.setDate(min.getDate() - 30); max.setDate(max.getDate() + 30);
    return { start: min, end: max };
  }, [projects]);
  
  const timelineStart = timelineBounds.start;
  const timelineEnd = timelineBounds.end;
  
  const timelineDays = useMemo(() => {
    const days: Date[] = [];
    const cursor = new Date(timelineStart);
    while (cursor <= timelineEnd) {
      days.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  }, [timelineStart, timelineEnd]);
  
  const monthSegments = useMemo(() => {
    const segments: { key: string; label: string; days: number }[] = [];
    const cursor = new Date(timelineStart.getFullYear(), timelineStart.getMonth(), 1);
    while (cursor <= timelineEnd) {
      const year = cursor.getFullYear(); const month = cursor.getMonth();
      const monthStart = new Date(year, month, 1); const monthEnd = new Date(year, month + 1, 0);
      const segmentStart = monthStart < timelineStart ? timelineStart : monthStart;
      const segmentEnd = monthEnd > timelineEnd ? timelineEnd : monthEnd;
      const days = getDayDiff(segmentEnd, segmentStart) + 1;
      segments.push({ key: `${year}-${month}`, label: formatMonthLabel(cursor), days });
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return segments;
  }, [timelineStart, timelineEnd]);

  const rowHeight = 64;
  const timelineWidth = Math.max(1, timelineDays.length * dayWidth);
  const dayMs = 1000 * 60 * 60 * 24;
  const timelineStartMs = timelineStart.getTime();
  const timelineEndMs = timelineEnd.getTime();
  
  const dateToPx = (date: Date | null) => {
    if (!date) return 0;
    const ms = clamp(date.getTime(), timelineStartMs, timelineEndMs);
    const offsetDays = (ms - timelineStartMs) / dayMs;
    return clamp(offsetDays * dayWidth, 0, timelineWidth);
  };
  
  const todayPx = dateToPx(new Date());

  const smoothScrollTo = (targetLeft: number) => {
    const headerEl = timelineScrollRef.current; const bodyEl = timelineBodyScrollRef.current;
    if (!headerEl || !bodyEl) return;
    const startLeft = headerEl.scrollLeft; const distance = targetLeft - startLeft; const duration = 400; 
    let startTime: number | null = null;
    isProgrammaticScrollRef.current = true; 

    const animation = (currentTime: number) => {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 4);
      const currentScroll = startLeft + (distance * ease);
      headerEl.scrollLeft = currentScroll; bodyEl.scrollLeft = currentScroll;

      if (timeElapsed < duration) { requestAnimationFrame(animation); } 
      else { setTimeout(() => { isProgrammaticScrollRef.current = false; }, 50); }
    };
    requestAnimationFrame(animation);
  };

  const scrollToDate = (dateString: string | null) => {
    if (!dateString) return;
    const targetDate = parseDate(dateString); if (!targetDate) return;
    const targetPx = dateToPx(targetDate);
    const headerEl = timelineScrollRef.current; if (!headerEl) return;
    const viewWidth = headerEl.clientWidth;
    const maxScroll = Math.max(0, headerEl.scrollWidth - viewWidth);
    const scrollTarget = clamp(targetPx - (viewWidth / 3), 0, maxScroll);
    smoothScrollTo(scrollTarget);
  };

  const scrollToToday = () => {
    const headerEl = timelineScrollRef.current; if (!headerEl) return;
    const viewWidth = headerEl.clientWidth || 0;
    const maxScroll = Math.max(0, headerEl.scrollWidth - viewWidth);
    const target = clamp(todayPx - viewWidth / 2, 0, maxScroll);
    smoothScrollTo(target);
  };

  // 🚨 [핵심 수정] 무조건 오늘 날짜로 찰떡같이 스냅하는 마법의 코드!
  useEffect(() => {
    // 로딩 중이거나, 세팅 모달창이 '열려있을 때'는 강제로 화면을 당기지 않음!
    if (isLoading || isSettingsOpen) return;

    const timer = setTimeout(() => {
      const headerEl = timelineScrollRef.current; 
      const bodyEl = timelineBodyScrollRef.current;
      if (!headerEl || !bodyEl) return;
      
      const viewWidth = headerEl.clientWidth || 0;
      const maxScroll = Math.max(0, headerEl.scrollWidth - viewWidth);

      // 의존성 배열에서 todayPx를 뺐으므로, 여기서 오늘 날짜 픽셀을 직접 계산합니다.
      const currentTodayPx = dateToPx(new Date()); 
      const target = clamp(currentTodayPx - viewWidth / 2, 0, maxScroll);
      
      // 즉각적으로 스크롤 위치를 꽂아버립니다.
      headerEl.scrollLeft = target;
      bodyEl.scrollLeft = target;
    }, 50);

    return () => clearTimeout(timer);
  }, [isLoading, zoomPercent, isSettingsOpen]);


  useEffect(() => {
    const onMouseUp = () => stopTimelineDrag();
    window.addEventListener('mouseup', onMouseUp);
    return () => window.removeEventListener('mouseup', onMouseUp);
  }, []);

  const syncHorizontalScroll = (source: 'header' | 'body') => {
    if (isSyncingScrollRef.current || isProgrammaticScrollRef.current) return;
    const headerEl = timelineScrollRef.current; const bodyEl = timelineBodyScrollRef.current;
    if (!headerEl || !bodyEl) return;
    isSyncingScrollRef.current = true;
    if (source === 'header') bodyEl.scrollLeft = headerEl.scrollLeft; else headerEl.scrollLeft = bodyEl.scrollLeft;
    isSyncingScrollRef.current = false;
  };
  
  const handleTimelineMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = e.currentTarget; if (!el) return;
    isDraggingTimelineRef.current = true; dragTargetScrollRef.current = el;
    dragStartXRef.current = e.clientX; dragStartScrollLeftRef.current = el.scrollLeft; e.preventDefault();
  };
  
  const handleTimelineMouseMove = (e: React.MouseEvent) => {
    const el = dragTargetScrollRef.current; if (!el || !isDraggingTimelineRef.current) return;
    const diff = e.clientX - dragStartXRef.current; el.scrollLeft = dragStartScrollLeftRef.current - diff;
    if (el === timelineScrollRef.current) syncHorizontalScroll('header'); else syncHorizontalScroll('body');
  };
  
  const stopTimelineDrag = () => { isDraggingTimelineRef.current = false; dragTargetScrollRef.current = null; };

  const moveProject = (fromProjectId: string, toProjectId: string) => {
    if (!fromProjectId || !toProjectId || fromProjectId === toProjectId) return;
    setProjects((prev) => {
      const fromIndex = prev.findIndex((p) => p.id === fromProjectId);
      const toIndex = prev.findIndex((p) => p.id === toProjectId);
      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return prev;
      
      const next = [...prev]; 
      const [moved] = next.splice(fromIndex, 1); 
      next.splice(toIndex, 0, moved);
      
      saveToServer(next, true); 
      return next;
    });
  };

  type TimelineRow = { key: string; type: 'project' | 'team'; project: ProjectProgress; team: TeamProgressItem; summary?: { delayedTeams: string[], imminentTeams: string[], overallStart: string, overallEnd: string } };
  
  const timelineRows = useMemo(() => {
    const rows: TimelineRow[] = [];
    filteredProjects.forEach((project) => {
      if (!project.teams || project.teams.length === 0) return;
      
      const summary = getProjectSummary(project);
      const projectDummyTeam: TeamProgressItem = {
        team: '총괄 일정', expectedStartDate: summary.overallStart, expectedEndDate: summary.overallEnd, completedDate: '', note: '', isUrgent: false
      };
      
      rows.push({ key: `${project.id}-project`, type: 'project', project, team: projectDummyTeam, summary });
      
      if (expandedProjectMap[project.id]) {
        project.teams
          .filter((team) => team.expectedStartDate || team.expectedEndDate || team.completedDate || team.isUrgent || team.note)
          .sort((a, b) => TEAM_ORDER.indexOf(a.team) - TEAM_ORDER.indexOf(b.team))
          .forEach((team) => rows.push({ key: `${project.id}-${team.team}`, type: 'team', project, team }));
      }
    });
    return rows;
  }, [filteredProjects, expandedProjectMap]);

  const weekdayText = ['일', '월', '화', '수', '목', '금', '토'];

  if (isLoading) return <div className="rounded-xl border border-slate-200 bg-white p-6 text-slate-500 font-bold">WBS 데이터를 불러오는 중입니다...</div>;

  if (projects.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="text-slate-700">등록된 프로젝트 데이터가 없습니다.</div>
        {/* 🛡️ 자물쇠 채우기: 첫 프로젝트 추가 버튼 */}
        <PermissionGate user={currentUser} action="wbs_edit">
          <button type="button" onClick={() => { addProject(); setIsSettingsOpen(true); }} className="mt-3 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700">
            <Plus size={16} /> 첫 프로젝트 추가하기
          </button>
        </PermissionGate>
      </div>
    );
  }

  const toggleProject = (projectId: string) => { setExpandedProjectMap((prev) => ({ ...prev, [projectId]: !prev[projectId] })); };

  // ============================================================================
  // 🎨 4. 화면 렌더링
  // ============================================================================
  return (
    <div className="space-y-5 animate-fade-in">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-xl font-black text-slate-900">팀별 진행상황 관리 (WBS)</h2>
            <p className="mt-1 text-sm text-slate-500">프로젝트별 일정, 완료일, 지연 사유를 한 화면에서 직관적으로 관리합니다.</p>
          </div>
          
          {activeFilter !== 'all' && (
            <button 
              onClick={() => setActiveFilter('all')}
              className="flex items-center gap-1.5 text-sm font-bold text-slate-500 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-colors"
            >
              <FilterX size={16} /> 필터 해제 (전체 보기)
            </button>
          )}
        </div>
        
        <div className="mt-4 flex flex-wrap gap-3 animate-fade-in">
          {globalSummary.urgent > 0 && (
            <button onClick={() => setActiveFilter(activeFilter === 'urgent' ? 'all' : 'urgent')} className={`flex items-center gap-2 px-4 py-2 rounded-xl shadow-sm transition-all border ${activeFilter === 'urgent' ? 'bg-purple-600 border-purple-700 text-white ring-4 ring-purple-100 scale-105' : 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100'}`}>
              <div className={`p-1.5 rounded-lg ${activeFilter === 'urgent' ? 'bg-white/20' : 'bg-purple-100'}`}><Zap size={18} className={activeFilter === 'urgent' ? 'text-white' : 'text-purple-600 animate-pulse'} /></div>
              <div className="text-sm font-bold">긴급 <span className="text-lg font-black">{globalSummary.urgent}</span>건</div>
            </button>
          )}
          {globalSummary.delayed > 0 && (
            <button onClick={() => setActiveFilter(activeFilter === 'delayed' ? 'all' : 'delayed')} className={`flex items-center gap-2 px-4 py-2 rounded-xl shadow-sm transition-all border ${activeFilter === 'delayed' ? 'bg-red-600 border-red-700 text-white ring-4 ring-red-100 scale-105' : 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'}`}>
              <div className={`p-1.5 rounded-lg ${activeFilter === 'delayed' ? 'bg-white/20' : 'bg-red-100'}`}><Flame size={18} className={activeFilter === 'delayed' ? 'text-white' : 'text-red-600 animate-pulse'} /></div>
              <div className="text-sm font-bold">지연 <span className="text-lg font-black">{globalSummary.delayed}</span>건</div>
            </button>
          )}
          {globalSummary.imminent > 0 && (
            <button onClick={() => setActiveFilter(activeFilter === 'imminent' ? 'all' : 'imminent')} className={`flex items-center gap-2 px-4 py-2 rounded-xl shadow-sm transition-all border ${activeFilter === 'imminent' ? 'bg-amber-500 border-amber-600 text-white ring-4 ring-amber-100 scale-105' : 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'}`}>
              <div className={`p-1.5 rounded-lg ${activeFilter === 'imminent' ? 'bg-white/20' : 'bg-amber-100'}`}><AlertCircle size={18} className={activeFilter === 'imminent' ? 'text-white' : 'text-amber-600'} /></div>
              <div className="text-sm font-bold">임박 <span className="text-lg font-black">{globalSummary.imminent}</span>건</div>
            </button>
          )}
          {globalSummary.in_progress > 0 && (
            <button onClick={() => setActiveFilter(activeFilter === 'in_progress' ? 'all' : 'in_progress')} className={`flex items-center gap-2 px-4 py-2 rounded-xl shadow-sm transition-all border ${activeFilter === 'in_progress' ? 'bg-blue-600 border-blue-700 text-white ring-4 ring-blue-100 scale-105' : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'}`}>
              <div className={`p-1.5 rounded-lg ${activeFilter === 'in_progress' ? 'bg-white/20' : 'bg-blue-100'}`}><Activity size={18} className={activeFilter === 'in_progress' ? 'text-white' : 'text-blue-600'} /></div>
              <div className="text-sm font-bold">정상 진행중 <span className="text-lg font-black">{globalSummary.in_progress}</span>건</div>
            </button>
          )}
          {globalSummary.completed > 0 && (
            <button onClick={() => setActiveFilter(activeFilter === 'completed' ? 'all' : 'completed')} className={`flex items-center gap-2 px-4 py-2 rounded-xl shadow-sm transition-all border ${activeFilter === 'completed' ? 'bg-emerald-600 border-emerald-700 text-white ring-4 ring-emerald-100 scale-105' : 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'}`}>
              <div className={`p-1.5 rounded-lg ${activeFilter === 'completed' ? 'bg-white/20' : 'bg-emerald-100'}`}><CheckCircle2 size={18} className={activeFilter === 'completed' ? 'text-white' : 'text-emerald-600'} /></div>
              <div className="text-sm font-bold">전체 완료 <span className="text-lg font-black">{globalSummary.completed}</span>건</div>
            </button>
          )}
        </div>
      </section>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
        <section className="xl:col-span-12 space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="text-lg font-black text-slate-900">프로젝트 타임라인 {activeFilter !== 'all' && <span className="text-blue-600 text-sm ml-2">(필터 적용됨)</span>}</div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="flex items-center bg-slate-100 rounded-lg p-1 border border-slate-200">
                  <button onClick={() => setZoomPercent(p => Math.max(10, p - 10))} className="p-1.5 text-slate-500 hover:bg-white hover:text-slate-800 rounded shadow-sm transition" title="축소 (넓게 보기)"><ZoomOut size={16} /></button>
                  <span className="text-xs font-black text-slate-700 px-2 w-12 text-center">{zoomPercent}%</span>
                  <button onClick={() => setZoomPercent(p => Math.min(100, p + 10))} className="p-1.5 text-slate-500 hover:bg-white hover:text-slate-800 rounded shadow-sm transition" title="확대 (자세히 보기)"><ZoomIn size={16} /></button>
                </div>
                <button type="button" onClick={scrollToToday} className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-blue-700 shadow-sm">오늘 이동</button>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200">
              <div className="grid grid-cols-[420px_1fr]">
                <div className="flex items-center justify-between border-r border-slate-700 bg-slate-900 px-5 py-4 rounded-tl-xl">
                  <span className="text-2xl font-black text-white">프로젝트 / 팀</span>
                  {/* 🛡️ 자물쇠 채우기: 프로젝트 추가 버튼 */}
                  <PermissionGate user={currentUser} action="wbs_edit">
                    <button type="button" onClick={() => { addProject(); setIsSettingsOpen(true); }} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-blue-700">
                      <Plus size={18} /> 추가
                    </button>
                  </PermissionGate>
                </div>

                <div ref={timelineScrollRef} className="overflow-x-auto bg-slate-900 text-white cursor-grab active:cursor-grabbing [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden rounded-tr-xl" onScroll={() => syncHorizontalScroll('header')} onMouseDown={handleTimelineMouseDown} onMouseMove={handleTimelineMouseMove} onMouseUp={stopTimelineDrag} onMouseLeave={stopTimelineDrag}>
                  <div style={{ width: `${timelineWidth}px` }}>
                    <div className="flex border-b border-slate-700">
                      {monthSegments.map((segment) => (
                        <div key={segment.key} className="border-r border-slate-700 py-3 text-center text-base font-black truncate" style={{ width: `${segment.days * dayWidth}px` }}>
                          {dayWidth > 15 ? segment.label : `${segment.key.split('-')[1]}월`}
                        </div>
                      ))}
                    </div>
                    <div className="flex">
                      {timelineDays.map((day, idx) => {
                        const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                        return (
                          <div key={`day-${idx}`} className={`border-r border-slate-700 py-2 text-center text-sm ${isWeekend ? 'bg-slate-700/70' : 'bg-slate-800/90'} overflow-hidden`} style={{ width: `${dayWidth}px` }}>
                            {dayWidth > 20 && (
                              <>
                                <div>{day.getDate()}</div>
                                <div className="text-xs text-slate-300">{weekdayText[day.getDay()]}</div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-[420px_1fr]">
                <div className="border-r-2 border-slate-700 bg-white">
                  {timelineRows.length === 0 && (
                    <div className="h-32 flex items-center justify-center text-slate-400 text-sm font-bold">조건에 맞는 프로젝트가 없습니다.</div>
                  )}
                  {timelineRows.map((row) => {
                    const isProject = row.type === 'project';
                    const isProjDone = isProjectCompleted(row.project); 
                    const status = getTeamStatus(row.team);
                    const done = getDdayLabel(row.team);
                    const projectIdx = filteredProjects.findIndex((project) => project.id === row.project.id);
                    
                    const projectTone = isProjDone ? 'bg-slate-100/50 opacity-70' : (projectIdx % 2 === 0 ? 'bg-slate-50/60' : 'bg-blue-50/40');
                    
                    return (
                      <div key={`left-${row.key}`} className={`h-16 border-b border-slate-200 px-3 py-2 ${isProject ? `${projectTone} border-t-2 border-t-slate-300` : projectTone} ${isProject && dragOverProjectId === row.project.id ? 'ring-2 ring-blue-400 ring-inset' : ''}`}>
                        {isProject ? (
                          <div
                            className="flex h-full items-center justify-between gap-2"
                            draggable onDragStart={(e) => { setDraggingProjectId(row.project.id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', row.project.id); }}
                            onDragEnd={() => { setDraggingProjectId(null); setDragOverProjectId(null); }}
                            onDragOver={(e) => { e.preventDefault(); if (draggingProjectId && draggingProjectId !== row.project.id) setDragOverProjectId(row.project.id); }}
                            onDragLeave={() => { if (dragOverProjectId === row.project.id) setDragOverProjectId(null); }}
                            onDrop={(e) => { e.preventDefault(); const fromId = e.dataTransfer.getData('text/plain') || draggingProjectId || ''; moveProject(fromId, row.project.id); setDraggingProjectId(null); setDragOverProjectId(null); }}
                          >
                            <div className="flex min-w-0 items-center gap-2">
                              <span title="마우스로 끌어서 순서 변경" className="flex items-center">
                                <GripVertical size={16} className="text-slate-300 cursor-grab active:cursor-grabbing hover:text-slate-500 transition-colors" />
                              </span>
                              <button type="button" onClick={(e) => { e.stopPropagation(); toggleProject(row.project.id); }} className="h-9 w-9 cursor-pointer rounded text-lg font-black text-slate-700 hover:bg-slate-200/60 flex-shrink-0">
                                {expandedProjectMap[row.project.id] ? '▾' : '▸'}
                              </button>
                              
                              <div className="min-w-0 flex flex-col justify-center cursor-pointer hover:opacity-70 transition-opacity" onClick={() => scrollToDate(row.summary?.overallStart || null)}>
                                <div className="truncate text-lg font-black text-slate-900 flex items-center gap-2">
                                  {isProjDone && <CheckCircle2 size={16} className="text-emerald-500 shrink-0"/>}
                                  {row.project.clientName || row.project.projectName}
                                </div>
                                <div className="truncate text-[11px] font-semibold text-slate-500 mt-0.5">
                                  총괄: {row.team.expectedStartDate || '?'} ~ {row.team.expectedEndDate || '?'}
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex flex-col items-end justify-center gap-1 flex-shrink-0 min-w-[70px]">
                              {/* 🛡️ 자물쇠 채우기: 설정 톱니바퀴 버튼 */}
                              <PermissionGate user={currentUser} action="wbs_edit">
                                <button type="button" onClick={(e) => { 
                                  e.stopPropagation(); 
                                  setSelectedProjectId(row.project.id); 
                                  originalProjectRef.current = JSON.parse(JSON.stringify(row.project));
                                  setIsSettingsOpen(true); 
                                }} className="rounded p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-800 transition-colors">
                                  <Settings size={16} />
                                </button>
                              </PermissionGate>
                              {!isProjDone && row.summary?.delayedTeams && row.summary.delayedTeams.length > 0 ? (
                                <div className="text-[10px] font-black text-rose-600 bg-rose-100 px-1.5 py-0.5 rounded truncate max-w-[120px] shadow-sm border border-rose-200">🚨 {row.summary.delayedTeams.join(', ')}</div>
                              ) : !isProjDone && row.summary?.imminentTeams && row.summary.imminentTeams.length > 0 ? (
                                <div className="text-[10px] font-black text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded truncate max-w-[120px] shadow-sm border border-amber-200">⚠️ {row.summary.imminentTeams.join(', ')}</div>
                              ) : null}
                            </div>
                          </div>
                        ) : (
                          <div className="flex h-full items-center justify-between">
                            <div className="pl-11 min-w-0 flex items-center gap-2">
                              <div>
                                <div className="text-sm font-bold text-slate-700 truncate flex items-center gap-1">
                                  {row.team.team}
                                  {row.team.isUrgent && <span className="px-1 py-0.5 bg-purple-600 text-white text-[9px] rounded font-black shadow-sm tracking-tight flex items-center gap-0.5"><Zap size={10}/> 긴급</span>}
                                </div>
                                <div className="text-[11px] font-medium text-slate-500 truncate mt-0.5">
                                  {row.team.expectedStartDate || '?'} ~ {row.team.expectedEndDate || '?'}
                                </div>
                              </div>
                            </div>
                            <div className={`rounded px-2 py-0.5 text-[11px] font-black flex-shrink-0 ml-2 tracking-tight shadow-sm border ${statusStyleMap[status]}`}>
                              {done}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div ref={timelineBodyScrollRef} className="relative overflow-x-auto bg-slate-50 cursor-grab active:cursor-grabbing" onScroll={() => syncHorizontalScroll('body')} onMouseDown={handleTimelineMouseDown} onMouseMove={handleTimelineMouseMove} onMouseUp={stopTimelineDrag} onMouseLeave={stopTimelineDrag}>
                  <div style={{ width: `${timelineWidth}px` }}>
                    {timelineRows.map((row, idx) => {
                      const projectIdx = filteredProjects.findIndex((project) => project.id === row.project.id);
                      const isProjDone = isProjectCompleted(row.project);
                      const fill = isProjDone ? 'bg-slate-100/30' : (projectIdx % 2 === 0 ? 'bg-slate-50/45' : 'bg-blue-50/35');
                      return (
                        <div key={`row-bg-${row.key}`} className={`absolute left-0 right-0 h-16 border-b border-slate-200 ${fill} ${row.type === 'project' ? 'border-t-2 border-t-slate-300' : ''}`} style={{ top: `${idx * rowHeight}px` }} />
                      );
                    })}
                    <div className="pointer-events-none absolute bottom-0 top-0">
                      <div className="flex h-full">
                        {timelineDays.map((day, idx) => {
                          const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                          return <div key={`grid-${idx}`} className={`h-full border-r border-slate-200 ${isWeekend ? 'bg-slate-100' : idx % 2 === 0 ? 'bg-white/50' : 'bg-slate-50/90'}`} style={{ width: `${dayWidth}px` }} />;
                        })}
                      </div>
                    </div>

                    <div className="pointer-events-none absolute bottom-0 top-0 w-[2px] bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)] z-10" style={{ left: `${todayPx}px` }} />

                    {timelineRows.map((row, idx) => {
                      const start = parseDate(row.team.expectedStartDate);
                      const end = parseDate(row.team.expectedEndDate);
                      const completed = parseDate(row.team.completedDate);
                      
                      let status = getTeamStatus(row.team);
                      if (row.type === 'project' && row.summary) {
                         if (row.summary.delayedTeams.length > 0) status = 'delayed';
                         else if (row.summary.imminentTeams.length > 0) status = 'imminent';
                      }

                      const startPx = dateToPx(start);
                      const endPx = dateToPx(end);
                      const widthPx = clamp(endPx - startPx, 8, timelineWidth);
                      const top = idx * rowHeight + 12;
                      const completedPx = dateToPx(completed);
                      
                      const isProject = row.type === 'project';
                      const isProjDone = isProjectCompleted(row.project);

                      let color = '';
                      if (isProjDone) { color = isProject ? 'bg-slate-300' : 'bg-slate-400'; } 
                      else {
                        color = status === 'completed' ? (isProject ? 'bg-emerald-400' : 'bg-emerald-500')
                          : status === 'delayed' ? (isProject ? 'bg-red-400' : 'bg-red-500')
                          : status === 'imminent' ? (isProject ? 'bg-amber-400' : 'bg-amber-500')
                          : status === 'upcoming' ? (isProject ? 'bg-slate-400' : 'bg-slate-500')
                          : (isProject ? 'bg-blue-400' : 'bg-blue-500');
                      }
                      
                      const urgentClass = (!isProject && row.team.isUrgent && status !== 'completed') ? 'ring-2 ring-purple-500 ring-offset-1 animate-pulse' : '';
                                
                      const label = isProject ? `[전체일정] ${row.project.clientName || row.project.projectName}` : `${row.team.team}`;
                      const hoverText = `${label}: ${row.team.expectedStartDate || '-'} ~ ${row.team.expectedEndDate || '-'}${row.team.note ? ` \n특이사항: ${row.team.note}` : ''}`;

                      return (
                        <div key={`bar-${row.key}`}>
                          {start && end && (
                            <button
                              type="button"
                              onClick={() => { if(!isProject) { 
                                setSelectedProjectId(row.project.id); 
                                originalProjectRef.current = JSON.parse(JSON.stringify(row.project));
                                setIsSettingsOpen(true); 
                              } }}
                              className={`absolute ${isProject ? 'h-8 mt-1 border-b-4 border-black/10' : 'h-10'} rounded-md px-2 text-center text-sm font-bold ${isProject ? 'leading-8' : 'leading-10'} text-white ${color} ${isProject ? 'cursor-default shadow-md' : 'shadow hover:brightness-110 transition-all'} ${urgentClass}`}
                              style={{ left: `${startPx}px`, top: `${top}px`, width: `${widthPx}px` }}
                              title={hoverText}
                            >
                              <span className="block truncate drop-shadow-md">{label}</span>
                            </button>
                          )}
                          {!isProject && completed && (
                            <div className={`absolute h-3.5 w-3.5 rounded-full border-2 border-white ${isProjDone ? 'bg-slate-500' : 'bg-emerald-700'} shadow`} style={{ left: `${completedPx - 6}px`, top: `${top + 10}px` }} title={`완료일 ${row.team.completedDate}${row.team.note ? ` | 특이사항: ${row.team.note}` : ''}`} />
                          )}
                        </div>
                      );
                    })}
                    <div style={{ height: `${timelineRows.length * rowHeight}px` }} />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-3 flex flex-wrap items-center gap-4 rounded-lg bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600 border border-slate-200">
              <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-red-500" />지연</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" />종료 임박(3일 이내)</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-blue-500" />진행중</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-slate-400" />예정 (일정 미정)</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />완료</span>
            </div>
          </div>
        </section>
      </div>

      {/* 🚨 [주의] 이 모달창 내부의 '저장/삭제' 버튼들은 이미 진입로(톱니바퀴)를 막았으므로 별도 자물쇠가 필요 없으나, 
          혹시 모를 상황을 대비해 모달 진입 자체에 PermissionGate 로직이 작동하게 설계되었습니다. */}
      {isSettingsOpen && selectedProject && (
        <div className="fixed inset-0 z-[90] bg-slate-900/40 p-4 md:p-8 animate-fade-in flex items-center justify-center">
          <div className="flex flex-col w-full max-w-[1400px] max-h-[90vh] rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
            
            <div className="px-6 py-4 border-b border-slate-200 bg-white flex items-center justify-between shrink-0">
              <h3 className="text-xl font-black text-slate-900 flex items-center gap-3">
                <Settings size={22} className="text-blue-600"/> 프로젝트 상세 설정
              </h3>
              <div className="flex items-center gap-2">
                <button onClick={() => setPendingDeleteProjectId(selectedProject.id)} className="inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-bold text-rose-700 hover:bg-rose-100 transition-colors"><Trash2 size={16} /> 프로젝트 삭제</button>
                <button onClick={() => setIsSettingsOpen(false)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"><X size={20} /></button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
              <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm mb-6">
                <div className="flex items-center justify-between mb-4">
                  <select value={selectedProjectId} onChange={(e) => setSelectedProjectId(e.target.value)} className="w-full max-w-sm rounded-lg border border-slate-300 bg-slate-50 px-3 py-2.5 text-sm font-bold text-slate-800 focus:ring-2 focus:ring-blue-100 outline-none cursor-pointer">
                    {projects.map((project) => <option key={project.id} value={project.id}>{(project.clientName || project.projectName) + ' - ' + (project.itemName || '품목 미입력')}</option>)}
                  </select>
                </div>
                
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div><label className="text-[11px] font-bold text-slate-500 mb-1 block">거래처명</label><input value={selectedProject.clientName} onChange={(e) => updateSelectedProjectMeta('clientName', e.target.value)} placeholder="예: 삼성전자" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-100 outline-none font-semibold text-slate-800" /></div>
                  <div><label className="text-[11px] font-bold text-slate-500 mb-1 block">품목명</label><input value={selectedProject.itemName} onChange={(e) => updateSelectedProjectMeta('itemName', e.target.value)} placeholder="예: 자동화 서버 랙" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-100 outline-none font-semibold text-slate-800" /></div>
                  <div><label className="text-[11px] font-bold text-slate-500 mb-1 block">마케팅 담당자</label><input value={selectedProject.marketingOwner || ''} onChange={(e) => updateSelectedProjectMeta('marketingOwner', e.target.value)} placeholder="예: 최광웅" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-100 outline-none font-semibold text-slate-800" /></div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col">
                <div className="p-4 border-b border-slate-200 bg-white flex items-center justify-between shrink-0">
                  <h4 className="text-lg font-black text-slate-900">팀별 세부 일정 입력</h4>
                  <div className="relative">
                    <button type="button" onClick={() => setShowTeamDropdown((prev) => !prev)} disabled={missingTeams.length === 0} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-xs font-bold text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-40"><Plus size={14} /> 팀 수동 추가</button>
                    {showTeamDropdown && missingTeams.length > 0 && (
                      <div className="absolute right-0 z-50 mt-2 w-32 rounded-lg border border-slate-200 bg-white p-1 shadow-xl">
                        {missingTeams.map((team) => <button key={team} type="button" onClick={() => { addTeamByName(team); setShowTeamDropdown(false); }} className="w-full rounded-md px-3 py-2 text-left text-sm font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-700">+ {team}</button>)}
                      </div>
                    )}
                  </div>
                </div>

                <div className="overflow-x-auto w-full">
                  <table className="w-full text-sm text-left border-collapse min-w-[950px]">
                    <thead className="bg-slate-100 text-slate-600 sticky top-0 z-20 shadow-sm border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 font-black w-24">팀</th>
                        <th className="px-4 py-3 font-black w-24">상태</th>
                        <th className="px-4 py-3 font-black w-[150px]">시작일</th>
                        <th className="px-4 py-3 font-black w-[150px]">종료일</th>
                        <th className="px-4 py-3 font-black w-[150px]">완료일</th>
                        <th className="px-4 py-3 font-black min-w-[200px]">특이사항 (메모)</th>
                        <th className="px-4 py-3 font-black w-16 text-center">긴급</th>
                        <th className="px-4 py-3 font-black w-16 text-center">기록</th>
                        <th className="px-4 py-3 font-black w-16 text-center">삭제</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {orderedTeams.map((team) => {
                        const status = getTeamStatus(team);
                        return (
                          <tr key={team.team} className="hover:bg-slate-50/50 transition-colors">
                            <td className="px-4 py-3 font-black text-slate-900 bg-white/50">{team.team}</td>
                            <td className="px-4 py-3 bg-white/50"><span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-black border ${statusStyleMap[status]}`}>{statusLabelMap[status]}</span></td>
                            <td className="px-4 py-3 bg-white/50"><input type="date" value={team.expectedStartDate} max={team.expectedEndDate || undefined} onChange={(e) => { const val = e.target.value; if (team.expectedEndDate && val > team.expectedEndDate) { alert('시작일은 종료일보다 늦을 수 없습니다!'); return; } updateTeamField(team.team, 'expectedStartDate', val); }} className="w-full rounded border border-slate-300 px-2 py-1.5 text-xs outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400" /></td>
                            <td className="px-4 py-3 bg-white/50"><input type="date" value={team.expectedEndDate} min={team.expectedStartDate || undefined} onChange={(e) => { const val = e.target.value; if (team.expectedStartDate && val < team.expectedStartDate) { alert('종료일은 시작일보다 빠를 수 없습니다!'); return; } updateTeamField(team.team, 'expectedEndDate', val); }} className="w-full rounded border border-slate-300 px-2 py-1.5 text-xs outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400" /></td>
                            <td className="px-4 py-3 bg-white/50"><input type="date" value={team.completedDate} onChange={(e) => updateTeamField(team.team, 'completedDate', e.target.value)} className="w-full rounded border border-emerald-300 bg-emerald-50/30 px-2 py-1.5 text-xs outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 text-emerald-800 font-bold" /></td>
                           <td className="px-4 py-3 bg-white/50">
  <input 
    type="text" 
    value={team.note} 
    onChange={(e) => updateTeamField(team.team, 'note', e.target.value)} 
    onKeyDown={async (e) => { 
      if (e.key === 'Enter') { 
        if(team.note.trim()) { 
          const userName = getCurrentUserName();
          
          // 1. 히스토리에 누적 저장
          const success = await crmDataService.addWbsTeamHistory(selectedProject.id, team.team, team.note, userName); 
          
          if (success) { 
            // 2. 🎯 [핵심수정] 현재 상태에서 입력칸 비우기
            updateTeamField(team.team, 'note', ''); 
            
            // 3. 🎯 [핵심수정] 서버에 저장된 note 필드도 빈 값으로 덮어쓰기 (그래야 새로고침해도 안 살아남)
            const updatedTeams = selectedProject.teams.map(t => 
              t.team === team.team ? { ...t, note: '' } : t
            );
            await crmDataService.saveProjectProgress(
              projects.map(p => p.id === selectedProject.id ? { ...p, teams: updatedTeams } : p)
            );

            // 4. 조용히 새로고침 (히스토리 모달 내용 갱신 등)
            onRefresh(); 
            
            alert(`[${team.team}] 진행상황이 히스토리에 저장되었습니다.`);
          } else { 
            alert("저장 실패"); 
          }
        }
      } 
    }} 
    placeholder="입력 후 Enter로 누적" 
    className="w-full rounded border border-slate-300 px-3 py-1.5 text-xs outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400" 
  />
</td>
                            <td className="px-4 py-3 text-center bg-white/50"><input type="checkbox" checked={team.isUrgent || false} onChange={(e) => updateTeamField(team.team, 'isUrgent', e.target.checked)} className="w-4 h-4 text-purple-600 border-slate-300 rounded focus:ring-purple-500 cursor-pointer" /></td>
                            <td className="px-4 py-3 text-center bg-white/50"><button type="button" onClick={() => setHistoryModal({ teamName: team.team, projectId: selectedProject.id })} className="p-1.5 text-indigo-500 hover:bg-indigo-50 rounded transition-colors" title="히스토리 누적 기록 보기"><MessageSquare size={16} /></button></td>
                            <td className="px-4 py-3 text-center bg-white/50"><button type="button" onClick={() => removeTeam(team.team)} className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded transition-colors" title="팀 정보 지우기"><Trash2 size={16} /></button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end shrink-0">
              <button onClick={handleSaveChangesWithAudit} disabled={isSaving} className="rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-bold text-white hover:bg-blue-700 shadow-sm transition-colors flex items-center gap-1.5 disabled:opacity-50">
                {isSaving ? <RefreshCw size={16} className="animate-spin"/> : <Save size={16} />}
                {isSaving ? '저장 중...' : '설정 완료 및 데이터 저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {historyModal && (
        <div className="fixed inset-0 z-[110] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-6 animate-fade-in">
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl flex flex-col max-h-[80vh] overflow-hidden border border-slate-200">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-white">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg text-indigo-700"><Clock3 size={18} /></div>
                <div><h4 className="font-black text-slate-900">{historyModal.teamName}팀 누적 기록</h4></div>
              </div>
              <button onClick={() => setHistoryModal(null)} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"><X size={18} className="text-slate-400" /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 bg-white custom-scrollbar">
              <div className="border-l-2 border-indigo-100 ml-3 pl-6 space-y-6">
                {isHistoryLoading ? (
                  <div className="text-center text-slate-400 font-bold py-10">기록을 불러오는 중...</div>
                ) : teamHistoryLogs.length === 0 ? (
                  <div className="text-center text-slate-400 font-bold py-10">등록된 과거 기록이 없습니다.</div>
                ) : (
                  teamHistoryLogs.map((log, idx) => (
                    <div key={idx} className="relative group">
                      <div className="absolute -left-[35px] top-1 w-4 h-4 bg-white border-4 border-indigo-500 rounded-full z-10 shadow-sm" />
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="flex items-center gap-1 bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md border border-slate-200"><UserIcon size={10} /><span className="text-[10px] font-black">{log.editor || '관리자'}</span></div>
                        <span className="text-[10px] font-bold text-slate-400">{log.date}</span>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 text-[13px] font-bold text-slate-700 leading-relaxed whitespace-pre-wrap">{log.content}</div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="p-5 bg-slate-50 border-t border-slate-100 flex gap-2 items-center">
               <input value={newHistoryContent} onChange={(e) => setNewHistoryContent(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleAddHistory(); }} placeholder="새로운 진행상황이나 메모를 남겨주세요..." className="flex-1 bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm font-bold outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" />
               <button onClick={handleAddHistory} disabled={!newHistoryContent.trim()} className="p-2.5 bg-indigo-600 text-white rounded-lg shadow-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"><Send size={18}/></button>
            </div>
          </div>
        </div>
      )}

      {pendingDeleteProjectId && (
        <div className="fixed inset-0 z-[98] bg-slate-900/50 backdrop-blur-sm p-4 flex items-center justify-center animate-fade-in">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl animate-fade-in-up">
            <div className="w-12 h-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mb-4"><AlertCircle size={24} /></div>
            <h4 className="text-xl font-black text-slate-900">프로젝트 완전 삭제</h4>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">정말 이 프로젝트를 영구적으로 삭제하시겠습니까?<br/>모든 하위 팀의 일정과 데이터가 즉시 삭제되며 <span className="text-rose-600 font-bold">복구할 수 없습니다.</span></p>
            <div className="mt-8 flex justify-end gap-3">
              <button type="button" onClick={() => setPendingDeleteProjectId(null)} className="rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 transition-colors">취소</button>
              <button type="button" onClick={() => { deleteProject(pendingDeleteProjectId); setPendingDeleteProjectId(null); setIsSettingsOpen(false); }} className="rounded-xl bg-rose-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-rose-700 shadow-md transition-colors">네, 삭제합니다</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectProgressBoard;